import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Landmark, Sparkles } from "lucide-react";
import { TAX_REGIMES } from "./tdsConstants";
import { saveRegimeSelection } from "./tdsStorage";
import { TDS_WORKSPACE_PATH } from "./tdsNavConfig";

const REGIME_DETAILS = {
  old: {
    title: "Old Tax Regime",
    description:
      "Suitable for employees claiming exemptions and deductions under various sections of the Income Tax Act.",
    highlights: ["HRA exemption", "80C / 80D deductions", "LTA & home loan interest", "Chapter VI-A benefits"],
    tone: "violet",
  },
  new: {
    title: "New Tax Regime",
    description:
      "Suitable for employees preferring simplified taxation with lower slab rates and limited deductions.",
    highlights: ["Lower slab rates", "Standard deduction", "Simplified compliance", "Fewer declarations"],
    tone: "emerald",
  },
};

export default function TdsRegimeLanding() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);

  const handleContinue = () => {
    if (!selected) return;
    saveRegimeSelection(selected);
    navigate(TDS_WORKSPACE_PATH, { replace: true });
  };

  return (
    <div className="tds-landing">
      <div className="tds-landing__welcome">
        <div className="tds-landing__eyebrow">
          <Sparkles size={12} />
          Welcome to TDS
        </div>
        <h1 className="tds-landing__title">Choose your organization&apos;s default tax regime</h1>
        <p className="tds-landing__subtitle">
          Start with a simple choice. We&apos;ll show only the features relevant to your selection — no
          complex tax screens until you&apos;re ready.
        </p>
      </div>

      <div className="tds-regime-grid" role="radiogroup" aria-label="Tax regime selection">
        {TAX_REGIMES.map((regime) => {
          const meta = REGIME_DETAILS[regime.value];
          const active = selected === regime.value;
          return (
            <button
              key={regime.value}
              type="button"
              role="radio"
              aria-checked={active}
              className={`tds-regime-card tds-regime-card--${meta.tone}${active ? " tds-regime-card--active" : ""}`}
              onClick={() => setSelected(regime.value)}
            >
              <div className="tds-regime-card__icon" aria-hidden="true">
                <Landmark size={28} strokeWidth={1.75} />
              </div>
              <h2 className="tds-regime-card__title">{meta.title}</h2>
              <p className="tds-regime-card__desc">{meta.description}</p>
              <ul className="tds-regime-card__list">
                {meta.highlights.map((item) => (
                  <li key={item}>
                    <Check size={14} strokeWidth={2.5} />
                    {item}
                  </li>
                ))}
              </ul>
              <span className="tds-regime-card__cta">
                {active ? "Selected" : `Select ${regime.label}`}
              </span>
            </button>
          );
        })}
      </div>

      <div className="tds-landing__actions">
        <button
          type="button"
          className="tds-btn tds-btn--primary tds-btn--lg"
          disabled={!selected}
          onClick={handleContinue}
        >
          Continue
        </button>
        {!selected && (
          <p className="tds-landing__hint">Select a tax regime above to continue.</p>
        )}
      </div>
    </div>
  );
}
